import { createStyles, rem } from '@mantine/core'
import { noop } from 'lodash'
import React, { CSSProperties, ReactNode, useContext, useState } from 'react'
import styled, { css } from 'styled-components'

import Icon from '../Icon'
import LoaderOverlay from '../LoaderOverlay'
import ThemeContext, { TTheme } from '../ThemeContext'
import NewManageDesignHeading from './NewManageDesignHeading'

type TWrapper = {
  flexed: boolean
  withShadow: boolean
}
const Wrapper = styled.div<TWrapper>`
  position: relative;

  ${({ flexed }) =>
    flexed
      ? css`
          display: flex;
          flex-direction: column;
          flex: 1;
        `
      : ''};

  box-shadow: ${({ withShadow }) =>
    withShadow ? '0px 0px 8px 0px rgba(0,0,0,0.5)' : ''};

  &:focus-within {
  }
`

type THeading = {
  theme: TTheme
  isExpanded: boolean
  withBorder: boolean
  withoutRadius?: boolean
}

const Heading = styled.div<THeading>`
  color: ${({ theme }) => theme.boxFontColor || theme.primaryColor};
  border-radius: 4px 4px ${({ isExpanded }) => (isExpanded ? '0 0' : '4px 4px')};
  ${({ withoutRadius }) => (withoutRadius ? 'border-radius: 0;' : '')}
  background: ${({ theme }) => theme.boxColor || theme.primaryBackgroundColor};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 10px;
  cursor: pointer;
  border: ${({ withBorder }) =>
    !withBorder ? 'none;' : '1px solid rgba(0, 0, 0, 0.125);'};
`
Heading.displayName = 'Heading'

const Badge = styled.div`
  background: white;
  border-radius: 5px;
  color: #36404a; // rgb(0, 174, 239);
  display: inline;
  margin-right: 10px;
  font-weight: bold;
  padding: 0 5px;
`

const Title = styled.div`
  font-weight: bold;
`

type TContent = {
  flexed: boolean
  transparent: boolean
  noHeader: boolean
  withHeaderBorder?: boolean
  newManageDesign?: boolean
}

const Content = styled.div<TContent>`
  position: relative;
  border: ${({ transparent, newManageDesign }) =>
    transparent || newManageDesign
      ? 'none;'
      : '1px solid rgba(0, 0, 0, 0.125);'}
  border-radius: ${({ noHeader, newManageDesign }) =>
    newManageDesign ? rem(12) : noHeader ? '4px' : '0'} ;
  background: ${({ transparent }) => (transparent ? 'none;' : '#fff;')}
  border-top: ${({ withHeaderBorder, newManageDesign }) =>
    withHeaderBorder || newManageDesign
      ? '0px solid white'
      : '1px solid rgba(0, 0, 0, 0.125)'};

  ${({ flexed }) =>
    flexed
      ? css`
          display: flex;
          flex-direction: column;
          flex: 1;
        `
      : ''};
`

type TCard = {
  className?: string
  children?: ReactNode
  title?: string
  badge?: string | number
  noHeader?: boolean
  notoggle?: boolean
  transparent?: boolean
  style?: CSSProperties
  withShadow?: boolean
  startExpanded?: boolean
  flexed?: boolean
  theme?: TTheme
  contentClass?: string
  fillHeight?: boolean
  customContentPaddingClass?: string
  contentStyles?: CSSProperties
  isLoading?: boolean
  skipTheFuckingRetardedDesignSeriouslyGuysThisShouldBeHandledByWrappers?: string
  withHeaderBorder?: boolean
  withoutRadius?: boolean
  newManageDesign?: boolean
}

const newManageDesignPaddingStyles = createStyles(() => ({
  padding: {
    padding: `${rem(16)} ${rem(12)}`,
  },
}))

const Card = (props: TCard): React.ReactElement => {
  const {
    title,
    children,
    badge,
    noHeader = false,
    notoggle,
    transparent = false,
    style,
    withShadow = false,
    startExpanded = true,
    flexed = false,
    contentClass,
    fillHeight = false, // force content to take 100% parent height,
    customContentPaddingClass,
    contentStyles = {},
    isLoading,
    skipTheFuckingRetardedDesignSeriouslyGuysThisShouldBeHandledByWrappers = '',
    withHeaderBorder = false,
    withoutRadius = false,
    // newManageDesign = false,
    className,
  } = props

  const newManageDesign = false // TODO: FIXME: @bob sollte ich disablen fürs erste

  const [isExpanded, setIsExpanded] = useState(startExpanded)
  const defaultTheme = useContext(ThemeContext)

  const { classes } = newManageDesignPaddingStyles()

  const toggle = (): void => setIsExpanded(!isExpanded)

  return (
    <Wrapper
      className={
        skipTheFuckingRetardedDesignSeriouslyGuysThisShouldBeHandledByWrappers ||
        'mb-2'
      }
      style={style || { height: fillHeight ? '100%' : 'auto' }}
      withShadow={withShadow}
      flexed={flexed}
      data-testid="card"
    >
      <LoaderOverlay isLoading={isLoading} />
      {!noHeader && !newManageDesign && (
        <Heading
          isExpanded={isExpanded}
          onClick={notoggle ? noop : toggle}
          theme={props.theme || defaultTheme}
          withBorder={withHeaderBorder}
          withoutRadius={withoutRadius}
        >
          <div className="d-flex" data-testid="card_title">
            {badge && <Badge>{badge}</Badge>}
            <Title>{title}</Title>
          </div>
          {!notoggle && (
            <Icon
              icon={`far fa-chevron-${isExpanded ? 'up' : 'down'}`}
              onClick={toggle}
            />
          )}
        </Heading>
      )}
      <Content
        flexed={flexed}
        hidden={!isExpanded}
        noHeader={noHeader}
        style={{ height: fillHeight ? '100%' : 'auto', ...contentStyles }}
        transparent={transparent}
        className={`${
          (newManageDesign ? classes.padding : customContentPaddingClass) ||
          'p-2'
        } ${contentClass || ''}`}
        newManageDesign={newManageDesign}
        withHeaderBorder={withHeaderBorder}
        data-testid="card_content"
      >
        {!noHeader && newManageDesign && (
          <NewManageDesignHeading badge={badge} title={title} />
        )}
        {children !== undefined ? children : null}
      </Content>
    </Wrapper>
  )
}

export default Card
